import { API } from '@/config/api';
import { apiClient } from '@/shared/api/apiClient';

import type {
  AddLinkedInCredentialRequest,
  LinkedInCredential,
  LoginSessionAnswer,
  PauseLinkedInCredentialRequest,
  ReplaceLinkedInCredentialCookiesRequest,
} from '../types';

/**
 * Every account the acting company has connected, newest first, **disconnected ones included**.
 *
 * <p>Returns the whole array: the endpoint is not paged (LA-10). A company with none answers
 * 200 with `[]`, which is a real answer and never a 404 (LA-12). The server owns the order and
 * nothing here re-sorts it.
 */
export async function fetchLinkedInCredentials(): Promise<LinkedInCredential[]> {
  return apiClient.get<LinkedInCredential[]>(API.linkedinAgentCredentials);
}

/** The accounts the agent may use: active ones only (LA-11). */
export async function fetchActiveLinkedInCredentials(): Promise<LinkedInCredential[]> {
  return apiClient.get<LinkedInCredential[]>(API.linkedinAgentActiveCredentials);
}

/**
 * Connects an account. Without cookies it is `pending` and not active until a login finishes
 * (LA-4); with them it is `active` at once (LA-5).
 */
export async function createLinkedInCredential(
  request: AddLinkedInCredentialRequest,
): Promise<LinkedInCredential> {
  return apiClient.post<LinkedInCredential>(API.linkedinAgentCredentials, request);
}

/** Replaces an account's cookies and reactivates it (LA-14). */
export async function replaceLinkedInCredentialCookies(
  externalId: string,
  request: ReplaceLinkedInCredentialCookiesRequest,
): Promise<LinkedInCredential> {
  return apiClient.patch<LinkedInCredential>(API.linkedinAgentCredential(externalId), request);
}

/** Disconnects an account: revoked and inactive together, nothing deleted (LA-16). */
export async function disconnectLinkedInCredential(externalId: string): Promise<void> {
  await apiClient.delete<unknown>(API.linkedinAgentCredential(externalId));
}

/**
 * Opens the browser agent's login window for an account, **reusing its saved browser profile**.
 *
 * <p>The cheap rung: the device LinkedIn already trusts is kept, which usually avoids a
 * verification challenge. See {@link ../loginSession} for when to climb to
 * {@link reconnectLinkedInCredential}.
 */
export async function setupLinkedInCredential(externalId: string): Promise<LoginSessionAnswer> {
  return apiClient.post<LoginSessionAnswer>(API.linkedinAgentCredentialSetup(externalId));
}

/**
 * Switches the account to a different LinkedIn login: **clears this account's saved browser
 * profile** and signs in clean (LA-22).
 *
 * <p>Destructive — it spends device trust that took time to earn — so it is only for evidence
 * that the saved session is dead, or when the user asked to switch.
 */
export async function reconnectLinkedInCredential(externalId: string): Promise<LoginSessionAnswer> {
  return apiClient.post<LoginSessionAnswer>(API.linkedinAgentCredentialReconnect(externalId));
}

/** Cancels a login the user closed the window on (LA-25). */
export async function cancelLinkedInLoginSession(
  externalId: string,
  sessionId: string,
): Promise<LoginSessionAnswer> {
  return apiClient.post<LoginSessionAnswer>(
    API.linkedinAgentCredentialCancel(externalId, sessionId),
  );
}

/** How the login is going, as the browser agent sees it (LA-27). */
export async function fetchLinkedInLoginStatus(externalId: string): Promise<LoginSessionAnswer> {
  return apiClient.get<LoginSessionAnswer>(API.linkedinAgentCredentialStatus(externalId));
}

/** Pauses automation for one account. A reason is required (LA-31). */
export async function pauseLinkedInCredential(
  externalId: string,
  request: PauseLinkedInCredentialRequest,
): Promise<LinkedInCredential> {
  return apiClient.post<LinkedInCredential>(API.linkedinAgentCredentialPause(externalId), request);
}

/** Resumes automation for one account (LA-33). */
export async function resumeLinkedInCredential(externalId: string): Promise<LinkedInCredential> {
  return apiClient.post<LinkedInCredential>(API.linkedinAgentCredentialResume(externalId));
}
